import type { ReactElement, ReactNode } from "react";
import { Button } from "@/components/base/buttons/button";
import { cx } from "@/utils/cx";
import { MessageGraphics, MessageGraphicsVariant } from "./MessageGraphics";

export const ErrorStateType = {
	Default: "default",
} as const;

export type ErrorStateType =
	(typeof ErrorStateType)[keyof typeof ErrorStateType];

export interface ErrorStateProps {
	/**
	 * Visual / contextual variant.
	 * @default "default"
	 */
	type?: ErrorStateType;
	/** Primary heading (e.g. "Something Went Wrong"). */
	title: ReactNode;
	/** Supporting description under the title. */
	description?: ReactNode;
	/**
	 * Whether to show the retry action button.
	 * @default true
	 */
	showAction?: boolean;
	/** Action button label. Defaults to "Try Again". */
	actionLabel?: string;
	/** Called when the action button is pressed. */
	onAction?: () => void;
	className?: string;
}

/**
 * Centered failure pattern when data cannot be loaded or rendered.
 *
 * Supports an optional graphic, heading, body text, and secondary CTA.
 * Swap copy to match the failure context.
 *
 * @see Figma ErrorState 3106:17652
 */
export function ErrorState({
	type = ErrorStateType.Default,
	title,
	description,
	showAction = true,
	actionLabel = "Try Again",
	onAction,
	className,
}: ErrorStateProps): ReactElement {
	return (
		<div
			className={cx(
				"mx-auto flex w-full max-w-[500px] min-h-[500px] flex-col items-center justify-center gap-5 px-6 py-12",
				className,
			)}
			data-testid="error-state"
			data-type={type}
			role="alert"
		>
			<MessageGraphics variant={MessageGraphicsVariant.Error} />
			<div className="flex w-full flex-col items-center gap-1 overflow-clip text-center">
				<p className="w-full truncate font-display text-md font-extrabold text-primary">
					{title}
				</p>
				{description != null ? (
					<p className="w-full text-sm font-medium text-secondary">
						{description}
					</p>
				) : null}
			</div>
			{showAction ? (
				<Button size="md" color="secondary" onClick={onAction}>
					{actionLabel}
				</Button>
			) : null}
		</div>
	);
}
